import React, { useState } from 'react';
import { BillingMode, BillingPlan } from '../billing/BillingApiClient';

const RED = '#C1121C';
const RED_DEEP = '#8E0C14';
const CREAM = '#FBF7F4';
// Gleiche Regel wie auf der Startseite: kein neutrales Schwarz auf Creme.
const INK = '#3D1418';
const serif = "'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif";
const sans = "system-ui, -apple-system, 'Segoe UI', sans-serif";

export type BillingPeriod = 'monthly' | 'yearly';

export interface Plan {
  id: 'free' | 'pro' | 'team';
  name: string;
  monthly: number;
  tagline: string;
  features: string[];
  highlight?: boolean;
  cta: string;
}

export const PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Frei',
    monthly: 0,
    tagline: 'Zum Ausprobieren und für öffentliche Geschichten.',
    features: ['Editor im Browser, ohne Installation', 'Lokal speichern, mit Konto in der Cloud', 'Öffentlicher Link zum Teilen'],
    cta: 'Ohne Konto starten',
  },
  {
    id: 'pro',
    name: 'Pro',
    monthly: 19,
    tagline: 'Für alle, die regelmäßig vortragen oder verkaufen.',
    features: ['Eigene 3D-Modelle und Video', 'Präsentation privat halten oder öffentlich teilen', 'Auf der eigenen Website einbetten', 'Vollbild-Vortragsmodus'],
    highlight: true,
    cta: 'Bald verfügbar',
  },
  {
    id: 'team',
    name: 'Team',
    monthly: 49,
    tagline: 'Gemeinsame Bibliothek, geteilte Vorlagen, Rollen.',
    features: ['Alles aus Pro', 'Gemeinsame Team-Bibliothek', 'Rollen: Ansehen, Bearbeiten, Verwalten'],
    cta: 'Bald verfügbar',
  },
];

export function planSlug(planId: Exclude<Plan['id'], 'free'>, period: BillingPeriod): string {
  return `presenter-${planId}-${period}`;
}

const card = (highlight?: boolean): React.CSSProperties => ({
  background: highlight ? RED : '#fff',
  color: highlight ? CREAM : INK,
  border: highlight ? `1px solid ${RED}` : '1px solid rgba(26,17,19,0.14)',
  borderRadius: 18,
  padding: '30px 26px 26px',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: highlight ? '0 22px 60px rgba(193,18,28,0.28)' : '0 8px 26px rgba(26,17,19,0.07)',
});

// Beträge bleiben bis zur Steuerberater-Auskunft ausgeblendet (Assistant, Post #4963).
// Die Stripe-Tarife existieren weiter, nur die Seite nennt keine Zahl.
const SHOW_AMOUNTS = false;

function displayPrice(plan: Plan, period: BillingPeriod, livePlans: BillingPlan[]): string {
  if (plan.id === 'free') return 'Kostenlos';
  if (!SHOW_AMOUNTS) return 'Preis folgt';
  const paidPlanId: 'pro' | 'team' = plan.id;
  const upstream = livePlans.find((item) => item.slug === planSlug(paidPlanId, period));
  const value = upstream ? upstream.price_cents / 100 : period === 'yearly' ? plan.monthly * 10 : plan.monthly;
  return new Intl.NumberFormat('de-AT', {
    style: 'currency',
    currency: upstream?.currency || 'EUR',
    maximumFractionDigits: value % 1 ? 2 : 0,
  }).format(value);
}

export interface PricingPageProps {
  billingMode?: BillingMode | null;
  livePlans?: BillingPlan[];
  activeTier?: string | null;
  busy?: boolean;
  error?: string | null;
  onSelectPlan?: (plan: Plan, period: BillingPeriod) => void;
}

const PricingPage: React.FC<PricingPageProps> = ({
  billingMode = null,
  livePlans = [],
  activeTier = null,
  busy = false,
  error = null,
  onSelectPlan,
}) => {
  const [period, setPeriod] = useState<BillingPeriod>('monthly');
  const narrow = typeof window !== 'undefined' && window.innerWidth < 860;

  const select = (plan: Plan) => {
    if (plan.id === 'free') {
      window.location.href = '/edit/new';
      return;
    }
    if (!SHOW_AMOUNTS) return;
    if (onSelectPlan) onSelectPlan(plan, period);
    else window.location.href = `/onboarding?plan=${plan.id}&period=${period}`;
  };

  return (
    <div style={{ position: 'absolute', inset: 0, overflowY: 'auto', background: CREAM, color: INK, fontFamily: sans, zIndex: 900 }}>
      <section style={{ padding: narrow ? '58px 22px 30px' : '86px 8vw 42px', textAlign: 'center' }}>
        <h1 style={{ fontFamily: serif, fontWeight: 400, fontSize: narrow ? 34 : 48, margin: '0 0 14px', color: RED_DEEP }}>Preise</h1>
        <p style={{ fontSize: 17, opacity: 0.72, maxWidth: 590, margin: '0 auto 26px', lineHeight: 1.55 }}>
          Ausprobieren kostet nichts und braucht kein Konto. Bezahlt wird erst, wenn du regelmäßig damit arbeitest.
        </p>
        {SHOW_AMOUNTS && <div style={{ display: 'inline-flex', padding: 4, borderRadius: 999, background: 'rgba(26,17,19,.07)' }}>
          {(['monthly', 'yearly'] as const).map((value) => (
            <button
              key={value}
              onClick={() => setPeriod(value)}
              style={{
                border: 0,
                borderRadius: 999,
                padding: '9px 16px',
                background: period === value ? '#fff' : 'transparent',
                color: period === value ? RED_DEEP : INK,
                boxShadow: period === value ? '0 4px 14px rgba(26,17,19,.08)' : 'none',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {value === 'monthly' ? 'Monatlich' : 'Jährlich · 2 Monate frei'}
            </button>
          ))}
        </div>}
      </section>

      <section style={{ padding: narrow ? '0 22px 60px' : '0 8vw 90px' }}>
        {error && (
          <div style={{ maxWidth: 740, margin: '0 auto 22px', border: '1px solid rgba(193,18,28,.25)', borderRadius: 12, padding: '12px 15px', background: 'rgba(193,18,28,.06)', color: RED_DEEP, fontSize: 13.5 }}>
            {error}
          </div>
        )}
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'repeat(3, 1fr)', gap: 22, alignItems: 'start' }}>
          {PLANS.map((plan) => {
            const current = activeTier === plan.id;
            return (
              <div key={plan.id} style={card(plan.highlight)}>
                {plan.highlight && (
                  <div style={{ alignSelf: 'flex-start', background: CREAM, color: RED_DEEP, fontSize: 11.5, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '5px 11px', borderRadius: 999, marginBottom: 14 }}>
                    Empfohlen
                  </div>
                )}
                <div style={{ fontFamily: serif, fontSize: 27, marginBottom: 6, color: plan.highlight ? CREAM : RED_DEEP }}>{plan.name}</div>
                <div style={{ fontSize: 14, opacity: plan.highlight ? 0.85 : 0.68, marginBottom: 20, lineHeight: 1.5, minHeight: 42 }}>{plan.tagline}</div>
                <div style={{ fontFamily: serif, fontSize: 38, marginBottom: 22 }}>
                  {displayPrice(plan, period, livePlans)}
                  {SHOW_AMOUNTS && plan.monthly > 0 && <span style={{ fontSize: 14, fontFamily: sans, opacity: 0.65 }}> / {period === 'yearly' ? 'Jahr' : 'Monat'}</span>}
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 26px', display: 'grid', gap: 10, flex: 1 }}>
                  {plan.features.map((feature) => (
                    <li key={feature} style={{ fontSize: 14.5, lineHeight: 1.45, display: 'flex', gap: 9 }}>
                      <span style={{ opacity: 0.55 }}>—</span><span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  disabled={busy || current || (!SHOW_AMOUNTS && plan.id !== 'free')}
                  onClick={() => select(plan)}
                  style={{
                    background: plan.highlight ? CREAM : RED,
                    color: plan.highlight ? RED_DEEP : '#fff',
                    border: 0,
                    borderRadius: 999,
                    padding: '13px 20px',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: busy || current || (!SHOW_AMOUNTS && plan.id !== 'free') ? 'default' : 'pointer',
                    width: '100%',
                    opacity: busy || current || (!SHOW_AMOUNTS && plan.id !== 'free') ? 0.55 : 1,
                  }}
                >
                  {current ? 'Aktueller Tarif' : plan.cta}
                </button>
              </div>
            );
          })}
        </div>

        <p style={{ textAlign: 'center', fontSize: 13.5, opacity: 0.65, marginTop: 34 }}>
          Hochgeladene Bilder, Videos und 3D-Modelle sind über ihren Link öffentlich abrufbar, auch wenn die Präsentation privat bleibt.
          <br />
          {SHOW_AMOUNTS ? 'Alle Preise zzgl. USt. · ' : 'Die Tarife Pro und Team werden gerade festgelegt. '}
          {SHOW_AMOUNTS && billingMode === 'live' && 'Sichere Zahlungsabwicklung über Stripe.'}
          {SHOW_AMOUNTS && billingMode === 'stripe_test' && 'Stripe-Testmodus: Es wird kein echtes Geld abgebucht.'}
          {!SHOW_AMOUNTS || !billingMode || billingMode === 'disabled' ? 'Zahlungsabwicklung wird vorbereitet.' : null}
        </p>
      </section>
    </div>
  );
};

export default PricingPage;
